// Eventos de ecommerce de GA4 (view_item → purchase). Hasta ahora GA4 solo
// contaba páginas vistas y la "compra" era ver /order-success, que también ven
// las transferencias sin pagar.

interface Ga4Item {
  item_id: string;
  item_name: string;
  price?: number;
  quantity?: number;
}

export interface Ga4EcommerceParams {
  currency?: string;
  value?: number;
  items?: Ga4Item[];
  transaction_id?: string;
  payment_type?: string;
}

type GtagFn = (...args: unknown[]) => void;

const getGtag = (): GtagFn | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return (window as unknown as { gtag?: GtagFn }).gtag ?? null;
};

export type Ga4EcommerceEvent = 'view_item' | 'add_to_cart' | 'begin_checkout' | 'add_payment_info' | 'purchase';

// gtag se carga con lazyOnload (components/Analytics.tsx), después del load de
// la página: un evento que sale apenas monta la pantalla (view_item, purchase)
// todavía no lo encuentra. Se reintenta hasta que aparezca. No se encola en
// dataLayer porque un 'event' que queda antes del 'config' se descarta.
const GTAG_RETRY_MS = 500;
const GTAG_MAX_RETRIES = 40;

export const trackGa4Event = (name: Ga4EcommerceEvent, params: Ga4EcommerceParams, attempt = 0): void => {
  const gtag = getGtag();
  if (!gtag) {
    if (typeof window !== 'undefined' && attempt < GTAG_MAX_RETRIES) {
      setTimeout(() => trackGa4Event(name, params, attempt + 1), GTAG_RETRY_MS);
    }
    return;
  }
  try {
    gtag('event', name, { currency: 'ARS', ...params });
  } catch (error) {
    console.error(`[GA4] Error tracking ${name}:`, error);
  }
};

const PURCHASE_TRACKED_PREFIX = 'purchase_tracked_';

// La compra se manda una sola vez por orden, aunque se recargue la pantalla de
// éxito o se vuelva a abrir el link. localStorage, no un ref: el ref se
// reinicia en cada recarga y por eso Meta contaba compras de más.
export const wasPurchaseTracked = (orderId: string): boolean => {
  try {
    return localStorage.getItem(`${PURCHASE_TRACKED_PREFIX}${orderId}`) === '1';
  } catch {
    return false;
  }
};

export const markPurchaseTracked = (orderId: string): void => {
  try {
    localStorage.setItem(`${PURCHASE_TRACKED_PREFIX}${orderId}`, '1');
  } catch {
    // Sin storage puede repetirse en una recarga; se acepta antes que perderla.
  }
};
